import React from 'react'
import classNames from 'classnames'
import { renderRichText } from 'gatsby-source-contentful/rich-text'
import { Icon } from '@toasttab/shared/src/utils/craftUtils'
import BadgeMDS from '@toasttab/shared/src/components/atoms/Badge/BadgeMDS'
import Image from '@toasttab/shared/src/components/atoms/Image/Image'
import VidyardVideoPlayer from '@toasttab/shared/src/components/molecules/VidyardVideo/VidyardVideoPlayer'
import VidyardPlayButton from '@toasttab/shared/src/components/molecules/VidyardPlayButton/VidyardPlayButton'
import Typography from '@toasttab/shared/src/components/atoms/Typography/Typography'
import TextLink from '@toasttab/shared/src/components/molecules/TextLink/TextLink'
import {
  TestimonialHeadingOptions,
  TestimonialInlineQuoteHeadingOptions
} from '@toasttab/shared/src/utils/Contentful/richText'
import { TestimonialCardProps, LinkProps } from './types.d'

const renderVideo = (
  vidyardId: string,
  vidyardInteraction: string,
  tabIndex: number,
  videoButtonClasses: string
) => {
  if (!vidyardId) return
  const videoButtonCss = classNames(
    'bg-white inline-flex items-center justify-center h-[56px] py-5 px-7 m-auto',
    videoButtonClasses
  )
  return (
    <div
      className='flex absolute top-0 bottom-0 left-0 right-0'
      data-testid='TestimonialCard-Video'
    >
      <VidyardPlayButton
        classes={videoButtonCss}
        ignoreSnowflakeStyling
        interactionId={vidyardInteraction}
        tabIndex={tabIndex}
        variation='secondary'
        onClick={async () => {
          if (typeof window !== 'undefined') {
            // @ts-ignore - Typescript defs missing for @vidyard/embed-code
            const vidyardEmbed = await import('@vidyard/embed-code')
            if (vidyardEmbed.api) {
              const players = vidyardEmbed.api.getPlayersByUUID(vidyardId)
              if (players && players.length > 0) {
                players[0].showLightbox()
              }
            }
          }
        }}
      />
      <div className='hidden'>
        <VidyardVideoPlayer vidyardId={vidyardId} />
      </div>
    </div>
  )
}

const renderImage = (image: any) => {
  if (!image) {
    return
  }
  const imgClasses = classNames(
    image[0].classes,
    'w-full h-full rounded-tl-md rounded-tr-md md:rounded-bl-md md:rounded-br-md testimonial-card--image'
  )
  return <Image {...image[0]} classes={imgClasses} />
}

const renderAuthorImage = (authorImage: any) => {
  if (!authorImage) {
    return
  }
  return (
    <Image
      classes='hidden lg:block'
      imageAlt={authorImage.title ? authorImage.title : null}
      url={authorImage.url ? authorImage.url : null}
      optimizedImages={
        authorImage.optimizedImages ? authorImage.optimizedImages : null
      }
    />
  )
}

const renderContentClasses = (
  isInlineQuote: boolean,
  alignInlineQuoteLeft: boolean,
  singleSlide: boolean
) => {
  return classNames({
    'flex flex-col col-span-4 md:col-span-6 p-4 md:p-0 mt-4 md:mt-0': true,
    'lg:pl-6': !isInlineQuote,
    'items-start justify-start': !isInlineQuote || alignInlineQuoteLeft,
    'lg:col-span-12 pt-10 md:pt-4 lg:pb-8': isInlineQuote,
    'lg:px-16': isInlineQuote && !alignInlineQuoteLeft,
    'text-center items-center': isInlineQuote && !alignInlineQuoteLeft,
    'lg:justify-center': singleSlide
  })
}

const renderAuthorContainerClasses = (
  isInlineQuote: boolean,
  alignInlineQuoteLeft: boolean
) => {
  return classNames('flex w-full', {
    'items-center md:items-start': !isInlineQuote && !alignInlineQuoteLeft,
    'items-center justify-center': isInlineQuote && !alignInlineQuoteLeft,
    'items-left md:items-start': alignInlineQuoteLeft,
    'max-w-[846px]': isInlineQuote
  })
}

const TestimonialCard = ({
  badge,
  bio,
  classes,
  ctaImage,
  ctaLabel,
  dataTestId = 'TestimonialCard-1',
  eyebrow,
  headline,
  icon,
  image,
  links = [],
  restaurantName,
  restaurantNewLine,
  sectionDescription,
  sectionHeading,
  singleSlide,
  slideIsActive,
  alignInlineQuoteLeft,
  toggleSwitch, // Drop Shadow (Mobile)
  toggleSwitch2, // Border (Mobile)
  videoButtonClasses,
  vidyardId,
  vidyardInteraction
}: TestimonialCardProps) => {
  const isInlineQuote = (!image || !image.length) && !vidyardId
  const cardClasses = classNames('md:ds-grid', classes, {
    'shadow-2xl md:shadow-none': toggleSwitch,
    'rounded-md border border-light md:rounded-none md:border-none':
      toggleSwitch2 && !isInlineQuote
  })

  let tabIndex = 0
  if (slideIsActive) {
    tabIndex = slideIsActive && !singleSlide ? 0 : -1
  }

  const renderImageVideo = () => {
    if (isInlineQuote) {
      return
    }
    return (
      <div className='h-[190px] md:h-auto col-span-4 md:col-span-6 relative [&>div:nth-child(1)]:z-10 [&>div:nth-child(2)]:z-0'>
        {renderVideo(
          vidyardId,
          vidyardInteraction,
          tabIndex,
          videoButtonClasses
        )}
        {renderImage(image)}
      </div>
    )
  }

  const iconCss = classNames('mb-6 testimonial-card--icon !w-auto md:mx-0', {
    'mx-auto': !alignInlineQuoteLeft
  })

  const contentCss = renderContentClasses(
    isInlineQuote,
    alignInlineQuoteLeft,
    singleSlide
  )

  const authorContainerCss = renderAuthorContainerClasses(
    isInlineQuote,
    alignInlineQuoteLeft
  )

  const headlineCss = classNames('mb-4 !font-normal md:text-left', {
    'text-center': !alignInlineQuoteLeft,
    'text-left': alignInlineQuoteLeft
  })

  const headingCss = classNames('mb-6 !font-normal md:text-left', {
    'text-center': !alignInlineQuoteLeft,
    'text-left': alignInlineQuoteLeft
  })

  const descriptionCss = classNames(
    'text-secondary font-normal content-start md:content-center mb-6 w-full',
    {
      'text-center': !alignInlineQuoteLeft,
      'text-left': alignInlineQuoteLeft,
      'md:text-left': !isInlineQuote || alignInlineQuoteLeft,
      'max-w-[846px]': isInlineQuote
    }
  )

  const authorCss = classNames(
    'w-100 md:mx-0 mb-6 md:mb-0 self-start justify-start  md:justify-start',
    {
      'text-center mx-auto': !alignInlineQuoteLeft,
      'text-left': alignInlineQuoteLeft,
      'md:text-left': !isInlineQuote || alignInlineQuoteLeft,
      '!text-[22px]': isInlineQuote && !alignInlineQuoteLeft
    }
  )

  const authorLabelCss = classNames('text-default', {
    '!font-effra !font-normal': isInlineQuote
  })

  const bioRestaurantContainerCss = classNames('block', {
    'md:flex': !restaurantNewLine,
    'mb-4 md:justify-start': links && links.length > 0,
    'pt-1': isInlineQuote,
    'md:justify-start': !alignInlineQuoteLeft && !isInlineQuote,
    'text-center justify-center': !alignInlineQuoteLeft,
    'text-left justify-start': alignInlineQuoteLeft
  })

  const restaurantNameCss = classNames('text-secondary tracking-[0.2px]', {
    block: restaurantNewLine,
    'inline md:inline-block ml-6 before:content-["|"] before:block before:absolute before:-left-[15px] before:-top-[2px]':
      !restaurantNewLine
  })

  return (
    <div className={cardClasses} data-testid={dataTestId} tabIndex={tabIndex}>
      {renderImageVideo()}
      <div className={contentCss}>
        {eyebrow ? (
          <div className='overline-text inline-flex items-center mb-2 font-normal'>
            {eyebrow}
          </div>
        ) : null}
        {icon && icon[0] ? (
          <Image
            {...icon[0]}
            classes={iconCss}
            objectCover={false}
            scaleHover={false}
          />
        ) : null}
        {badge ? <BadgeMDS {...badge} classes='mb-3' /> : null}
        {headline ? (
          <Typography as='p' classes={headlineCss} variation='h3'>
            {headline}
          </Typography>
        ) : null}

        {sectionHeading && !sectionHeading?.raw ? (
          <Typography as='p' classes={headingCss} variation='h4'>
            {sectionHeading}
          </Typography>
        ) : null}

        {sectionHeading?.raw
          ? renderRichText(
              sectionHeading,
              isInlineQuote
                ? TestimonialInlineQuoteHeadingOptions(
                    alignInlineQuoteLeft ? 'left' : 'center'
                  )
                : TestimonialHeadingOptions
            )
          : null}

        {sectionDescription ? (
          <Typography as='p' classes={descriptionCss} variation='default'>
            {sectionDescription}
          </Typography>
        ) : null}
        <div
          className={authorContainerCss}
          data-testid='TestimonialCard-author'
        >
          {ctaImage?.length ? renderAuthorImage(ctaImage[0]) : null}
          <div className={authorCss}>
            {ctaLabel ? (
              <Typography as='p' classes={authorLabelCss} variation='h5'>
                {ctaLabel}
              </Typography>
            ) : null}

            {bio || restaurantName ? (
              <div className={bioRestaurantContainerCss}>
                {bio ? (
                  <Typography
                    as='span'
                    classes='text-secondary relative inline md:inline-block tracking-[0.2px]'
                    variation='default'
                  >
                    {bio}
                  </Typography>
                ) : null}
                {bio && restaurantName ? (
                  <span
                    aria-hidden='true'
                    className='text-secondary inline md:inline-block mx-3 tracking-[0.2px]'
                  >
                    |
                  </span>
                ) : null}
                {restaurantName ? (
                  <Typography
                    as='span'
                    classes={restaurantNameCss}
                    variation='default'
                  >
                    {restaurantName}
                  </Typography>
                ) : null}
              </div>
            ) : null}
            {links && links.length > 0
              ? links.map((item: LinkProps, index: number) => {
                  return (
                    <TextLink
                      classes={`block ${!isInlineQuote && 'md:w-full'}`}
                      href={item.ctaLink}
                      key={index}
                      tabIndex={tabIndex}
                      text={item.ctaLabel}
                      icon={Icon.linkIcons(
                        item.linkIcons ? item.linkIcons : 'none'
                      )}
                    />
                  )
                })
              : null}
          </div>
        </div>
      </div>
    </div>
  )
}

export default TestimonialCard
